/**
 * What the hub (or an invite email) put in the address bar fragment, read defensively:
 *
 *   #signin-error=<code>&login=<value>&provider=<id>   a refused sign-in (GitHub sends no provider)
 *   #invite=<secret>                                   an invite link; the secret never reaches a server
 *
 * Nothing here trusts the text: unknown codes become a generic one, an unknown provider is
 * dropped, and `login` is kept only when it looks like an email or a GitHub login.
 */
import { isSignInErrorCode, isSignInProvider } from '@orgus/protocol';
import type { SignInProviderId } from '@orgus/protocol';

export interface SigninError {
  /** One of SIGN_IN_ERROR_CODES or 'unknown' from the hash; 'bad-link' or 'rate-limited' from a bad owner link. */
  code: string;
  login: string | null;
  provider: SignInProviderId | null;
  /** An owner link from the address bar that did not work, to show it again in the form. */
  link?: string;
}

const EMAIL = /^[^\s@]{1,64}@[^\s@]{1,190}$/;
const GITHUB_LOGIN = /^[a-z0-9](?:[a-z0-9-]{0,38})$/i;
const INVITE = /^[A-Za-z0-9_-]{20,100}$/;

function decode(v: string | undefined): string | null {
  if (v === undefined) return null;
  try {
    return decodeURIComponent(v.replace(/\+/g, ' '));
  } catch {
    return null;
  }
}

function params(hash: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const part of hash.replace(/^#/, '').split('&')) {
    const i = part.indexOf('=');
    if (i <= 0) continue;
    const k = part.slice(0, i);
    if (!out.has(k)) out.set(k, part.slice(i + 1));
  }
  return out;
}

/** True when `v` is safe to show as the account in a sign-in error. */
export function isShowableLogin(v: string): boolean {
  if (v.length > 254 || /[\u0000-\u001f\u007f<>"'`]/.test(v)) return false;
  return EMAIL.test(v) || GITHUB_LOGIN.test(v);
}

export function parseSigninError(hash: string): SigninError | null {
  const p = params(hash);
  if (!p.has('signin-error')) return null;
  try {
    let code = decode(p.get('signin-error')) ?? '';
    const rawProvider = decode(p.get('provider'));
    let provider: SignInProviderId | null = isSignInProvider(rawProvider) ? rawProvider : null;
    // GitHub's own refusals carry no provider; its exchange failure has its own code.
    if (code === 'github') {
      code = 'idp-error';
      provider = 'github';
    }
    if (!provider && !p.has('provider')) provider = 'github';
    if (!isSignInErrorCode(code)) code = 'unknown';
    const rawLogin = decode(p.get('login'));
    const login = rawLogin && isShowableLogin(rawLogin) ? rawLogin : null;
    return { code, login, provider };
  } catch {
    return { code: 'unknown', login: null, provider: null };
  }
}

/** The invite secret from `#invite=<secret>`, or null. */
export function parseInvite(hash: string): string | null {
  const v = decode(params(hash).get('invite'));
  return v && INVITE.test(v) ? v : null;
}

/** True when the fragment carries something this module reads (and should strip). */
export function hasAuthFragment(hash: string): boolean {
  const p = params(hash);
  return p.has('signin-error') || p.has('invite');
}
